<template>
  <div class="w-screen min-h-screen bg-white relative pb-20">
    <div class="w-full h-full overflow-y-auto overflow-x-hidden bg-white relative">
      <div class="w-full bg-white px-4 py-3 border-b border-gray-200 flex justify-between items-center print:hidden">
        <div class="font-bold text-blue-600">E-TRANSAKSI</div>
        <svg @click="logout" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 stroke-current text-red-400 cursor-pointer" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      </div>

      <div class="px-4 pt-4" id="printable-area">
        <!-- Title -->
        <div class="flex justify-between items-center mb-4">
          <div class="text-center w-full relative">
            <h5 class="font-bold text-blue-600">CATATAN GAJI & BON</h5>
            <h6 class="font-bold text-red-500">EF-PRINT</h6>
          </div>
        </div>
        <div class="mb-6 p-4 border rounded-lg bg-gray-50 shadow-sm print:hidden flex flex-col sm:flex-row justify-between items-center gap-4">
          <div class="flex items-center gap-2">
            <label class="text-xs font-bold">Bulan:</label>
            <input type="month" v-model="currentDate" @change="fetchPremiData" class="border rounded px-2 py-1 text-sm bg-white">
          </div>
          <div class="flex gap-2">
            <button @click="$router.push({ name: 'CreateSalary' })" class="bg-green-600 text-white px-4 py-2 rounded-md font-semibold text-xs hover:bg-green-700">
              + Tambah Gaji
            </button>
            <button @click="showModal = true" class="bg-blue-600 text-white px-4 py-2 rounded-md font-semibold text-xs hover:bg-blue-700">
              + Tambah Premi
            </button>
          </div>
        </div>

        <div v-if="employees.length > 0" class="border border-gray-300 rounded p-3 mb-4 bg-gray-50 text-xs">
          <div class="flex justify-between mb-1">
            <span>Total Gaji Karyawan</span>
            <span class="font-bold">{{ formatCurrency(totalGaji) }}</span>
          </div>
          <div class="flex justify-between mb-1">
            <span>Total Premi (Sudah Dikeluarkan)</span>
            <span class="font-bold text-red-500">{{ formatCurrency(totalPremi) }}</span>
          </div>
          <div class="flex justify-between border-t border-gray-300 pt-1 mt-1">
            <span class="font-bold">Sisa Belum Dibayarkan</span>
            <span class="font-bold text-green-600">{{ formatCurrency(totalSisa) }}</span>
          </div>
        </div>

        <!-- Create Modal -->
        <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 print:hidden">
          <div class="bg-white p-6 rounded-lg w-96 shadow-xl">
            <h3 class="font-bold text-lg mb-4 text-blue-600">Tambah Premi Baru</h3>

            <div class="mb-3">
              <label class="block text-xs font-bold mb-1">Karyawan</label>
              <div class="relative">
                <input
                  type="text"
                  v-model="userSearch"
                  @input="searchUsers"
                  @focus="onUserFocus"
                  @blur="hideUserDropdownDelayed"
                  class="w-full border p-2 rounded text-sm"
                  placeholder="Cari nama karyawan..."
                >
                <div v-if="showUserDropdown && filteredUserList.length > 0" class="absolute w-full bg-white border border-gray-300 mt-1 max-h-60 overflow-y-auto shadow-lg z-10 rounded-md">
                  <div
                    v-for="user in filteredUserList"
                    :key="user.id"
                    @click="selectUser(user)"
                    class="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm border-b last:border-b-0"
                  >
                    {{ user.label }}
                  </div>
                </div>
              </div>
              <div v-if="selectedUser" class="mt-1 text-xs text-green-600 font-bold">
                Dipilih: {{ selectedUser.label }}
              </div>
            </div>

            <div class="mb-3">
              <label class="block text-xs font-bold mb-1">Tanggal</label>
              <input type="date" v-model="form.tgl_premi" class="w-full border p-2 rounded text-sm">
            </div>

            <div class="mb-3">
              <label class="block text-xs font-bold mb-1">Nama Premi</label>
              <input type="text" v-model="form.nama_premi" class="w-full border p-2 rounded text-sm" placeholder="Contoh: Pinjaman, Bon">
            </div>

            <div class="mb-3">
              <label class="block text-xs font-bold mb-1">Nominal (Rp)</label>
              <input type="number" v-model="form.harga" class="w-full border p-2 rounded text-sm">
            </div>

            <div class="mb-3">
              <label class="block text-xs font-bold mb-1">Keterangan</label>
              <textarea v-model="form.keterangan" class="w-full border p-2 rounded text-sm" rows="2"></textarea>
            </div>

            <div class="flex justify-end gap-2 mt-4">
              <button @click="closeModal" class="px-4 py-2 text-gray-600 text-sm hover:bg-gray-100 rounded">Batal</button>
              <button @click="submitPremi" class="px-4 py-2 bg-blue-600 text-white text-sm hover:bg-blue-700 rounded">Simpan</button>
            </div>
          </div>
        </div>

        <!-- Loop through Employees -->
        <div v-for="(employee, index) in employees" :key="index" class="mb-6">
          <h3 class="font-bold text-sm mb-1 uppercase">{{ index + 1 }}. {{ employee.nama }}</h3>
          <div v-if="employee.gaji" class="text-xs mb-2 text-gray-600">
            Sisa Gaji: <span class="font-bold text-green-600">{{ formatCurrency(employee.gaji.sisa_gaji) }}</span>
            <span class="text-gray-400 ml-1">(Produktif: {{ formatCurrency(employee.gaji.gaji_produktif) }} + Absen: {{ formatCurrency(employee.gaji.gaji_absen_siang + employee.gaji.gaji_absen_malam) }} - Premi: {{ formatCurrency(employee.gaji.total_premi) }})</span>
          </div>

          <div class="mb-4 overflow-x-auto">
            <table class="w-full border-collapse border border-gray-400 text-xs">
              <thead>
                <tr>
                  <th class="border border-gray-400 px-1 py-1 w-8 text-center">NO</th>
                  <th class="border border-gray-400 px-1 py-1 w-24 text-center">TANGGAL</th>
                  <th class="border border-gray-400 px-1 py-1 text-center font-bold">PREMI</th>
                  <th class="border border-gray-400 px-1 py-1 text-center font-bold">NOMINAL</th>
                  <th class="border border-gray-400 px-1 py-1 text-center font-bold">KETERANGAN</th>
                  <th class="border border-gray-400 px-1 py-1 text-center font-bold print:hidden">AKSI</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!employee.premis || employee.premis.length === 0">
                  <td colspan="6" class="border border-gray-400 px-1 py-1 text-center text-gray-500 italic">Tidak ada data premi</td>
                </tr>
                <tr v-for="(premi, pIndex) in employee.premis" :key="pIndex">
                  <td class="border border-gray-400 px-1 py-1 text-center">{{ pIndex + 1 }}</td>
                  <td class="border border-gray-400 px-1 py-1 text-center">{{ formatDate(premi.tgl_premi) }}</td>
                  <td class="border border-gray-400 px-1 py-1 text-center">{{ premi.nama_premi }}</td>
                  <td class="border border-gray-400 px-1 py-1 text-right">{{ formatCurrency(premi.harga) }}</td>
                  <td class="border border-gray-400 px-1 py-1">{{ premi.keterangan }}</td>
                  <td class="border border-gray-400 px-1 py-1 text-center print:hidden whitespace-nowrap">
                    <button @click="editPremi(premi)" class="text-blue-600 hover:text-blue-800 mr-1" title="Edit">✏️</button>
                    <button @click="deletePremi(premi.id)" class="text-red-600 hover:text-red-800" title="Hapus">🗑️</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Edit Modal -->
        <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 print:hidden">
          <div class="bg-white p-6 rounded-lg w-96 shadow-xl">
            <h3 class="font-bold text-lg mb-4 text-blue-600">Edit Premi</h3>

            <div class="mb-3">
              <label class="block text-xs font-bold mb-1">Tanggal</label>
              <input type="date" v-model="editForm.tgl_premi" class="w-full border p-2 rounded text-sm">
            </div>

            <div class="mb-3">
              <label class="block text-xs font-bold mb-1">Nama Premi</label>
              <input type="text" v-model="editForm.nama_premi" class="w-full border p-2 rounded text-sm">
            </div>

            <div class="mb-3">
              <label class="block text-xs font-bold mb-1">Nominal (Rp)</label>
              <input type="number" v-model="editForm.harga" class="w-full border p-2 rounded text-sm">
            </div>

            <div class="mb-3">
              <label class="block text-xs font-bold mb-1">Keterangan</label>
              <textarea v-model="editForm.keterangan" class="w-full border p-2 rounded text-sm" rows="2"></textarea>
            </div>

            <div class="flex justify-end gap-2 mt-4">
              <button @click="showEditModal = false" class="px-4 py-2 text-gray-600 text-sm hover:bg-gray-100 rounded">Batal</button>
              <button @click="submitEditPremi" class="px-4 py-2 bg-blue-600 text-white text-sm hover:bg-blue-700 rounded">Update</button>
            </div>
          </div>
        </div>

      </div>
    </div>
    <bottom-nav></bottom-nav>
  </div>
</template>

<script>
import BottomNav from '../components/BottomNav.vue'
import axios from 'axios' // Ensure axios is available, likely already installed in the project context

export default {
  name: 'Salary',
  components: { BottomNav },
  data () {
    return {
      employees: [],
      currentDate: new Date().toISOString().slice(0, 7), // YYYY-MM format
      showModal: false,
      showEditModal: false,
      userSearch: '',
      userList: [],
      allUserList: [],
      showUserDropdown: false,
      selectedUser: null,
      form: {
        user_id: '',
        tgl_premi: new Date().toISOString().slice(0, 10),
        nama_premi: '',
        harga: '',
        keterangan: ''
      },
      editForm: {
        id: '',
        tgl_premi: '',
        nama_premi: '',
        harga: '',
        keterangan: ''
      }
    }
  },
  computed: {
    token () {
      return this.$store.state.token
    },
    filteredUserList () {
      if (!this.userSearch) {
        return this.allUserList
      }
      const query = this.userSearch.toLowerCase()
      return this.allUserList.filter(u => u.label.toLowerCase().includes(query))
    },
    totalGaji () {
      return this.employees.reduce((sum, e) => sum + (e.gaji ? e.gaji.total_gaji : 0), 0)
    },
    totalPremi () {
      return this.employees.reduce((sum, e) => sum + (e.gaji ? e.gaji.total_premi : 0), 0)
    },
    totalSisa () {
      return this.employees.reduce((sum, e) => sum + (e.gaji ? e.gaji.sisa_gaji : 0), 0)
    }
  },
  methods: {
    logout () {
      localStorage.removeItem('X-FPRINT-TOKEN')
      localStorage.removeItem('X-FPRINT-USER')
      this.$store.dispatch('set_token', null)
      this.$store.dispatch('set_user', null)
      this.$router.push({ name: 'Login' })
    },
    async fetchPremiData () {
      try {
        // Assuming base URL is configured in axios or relative path works
        // Using relative path based on typical Vue CLI proxy or production build
        const response = await axios.get(`e_kinerja/laporan/get_premi_group_by_user?bulan=${this.currentDate}&token=${this.token}`)
        this.employees = response.data
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    },
    async fetchAllUsers () {
      if (this.allUserList.length > 0) return // Already loaded
      try {
        const response = await axios.get(`e_kinerja/laporan/get_users_search?term=&token=${this.token}&t=` + new Date().getTime())
        this.allUserList = response.data
      } catch (error) {
        console.error('Error fetching users:', error)
      }
    },
    async onUserFocus () {
      await this.fetchAllUsers()
      this.showUserDropdown = true
    },
    searchUsers () {
      this.showUserDropdown = true
    },
    hideUserDropdownDelayed () {
      setTimeout(() => {
        this.showUserDropdown = false
      }, 200)
    },
    selectUser (user) {
      this.selectedUser = user
      this.form.user_id = user.id
      this.userSearch = user.label
      this.showUserDropdown = false
    },
    closeModal () {
      this.showModal = false
      this.resetForm()
    },
    resetForm () {
      this.userSearch = ''
      this.selectedUser = null
      this.showUserDropdown = false
      this.form = {
        user_id: '',
        tgl_premi: new Date().toISOString().slice(0, 10),
        nama_premi: '',
        harga: '',
        keterangan: ''
      }
    },
    async submitPremi () {
      if (!this.form.user_id || !this.form.nama_premi || !this.form.harga) {
        alert('Mohon lengkapi data (Karyawan, Nama Premi, Nominal)')
        return
      }

      try {
        const response = await axios.post('e_kinerja/laporan/store_premi?token=' + this.token, this.form)
        if (response.data.status === 'success') {
          alert('Premi berhasil ditambahkan')
          this.closeModal()
          this.fetchPremiData() // Refresh table
        } else {
          alert('Gagal menyimpan premi')
        }
      } catch (error) {
        console.error('Error submitting premi:', error)
        alert('Terjadi kesalahan saat menyimpan data.')
      }
    },
    editPremi (premi) {
      this.editForm = {
        id: premi.id,
        tgl_premi: premi.tgl_premi ? premi.tgl_premi.slice(0, 10) : '',
        nama_premi: premi.nama_premi,
        harga: premi.harga,
        keterangan: premi.keterangan
      }
      this.showEditModal = true
    },
    async submitEditPremi () {
      try {
        const response = await axios.post('e_kinerja/laporan/update_premi?token=' + this.token, this.editForm)
        if (response.data.status === 'success') {
          alert('Premi berhasil diupdate')
          this.showEditModal = false
          this.fetchPremiData()
        } else {
          alert('Gagal mengupdate premi')
        }
      } catch (error) {
        console.error('Error updating premi:', error)
        alert('Terjadi kesalahan saat mengupdate data.')
      }
    },
    async deletePremi (id) {
      if (!confirm('Yakin ingin menghapus premi ini?')) return
      try {
        const response = await axios.post('e_kinerja/laporan/delete_premi?token=' + this.token, { id: id })
        if (response.data.status === 'success') {
          alert('Premi berhasil dihapus')
          this.fetchPremiData()
        } else {
          alert('Gagal menghapus premi')
        }
      } catch (error) {
        console.error('Error deleting premi:', error)
        alert('Terjadi kesalahan saat menghapus data.')
      }
    },
    formatCurrency (value) {
      if (!value) return ''
      return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(value)
    },
    formatDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: 'short' }).format(date)
    }
  },
  mounted () {
    this.fetchPremiData()
  }
}
</script>
